import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FaPlus, FaUsers } from 'react-icons/fa';
import personalBadgeIcon from '../../assets/icons/personal-badge.svg';
import eyeIcon from '../../assets/icons/eye.svg';
import shareFatIcon from '../../assets/icons/share-fat.svg';
import notePencilIcon from '../../assets/icons/note-pencil.svg';
import trashIcon from '../../assets/icons/trash.svg';
import { notify } from '../../components/CustomToast';
import { useNotes } from '../../hooks/useNotes';
import noteService from '../../services/noteService';
import NoteFormModal from './NoteFormModal';
import NoteShareModal from './NoteShareModal';
import NoteViewModal from './NoteViewModal';
import DeleteNoteModal from './DeleteNoteModal';
import NotesSkeleton from './NotesSkeleton';
import { useI18n } from '../../i18n';
import BackButton from '../Common/BackButton';
import { useDashboard } from '../../context/DashboardContext';
import './NotesSection.css';

const NOTE_COLORS = [
  'note-color-yellow',
  'note-color-red',
  'note-color-blue',
  'note-color-green',
  'note-color-purple',
];

const getNoteColor = (index, prevColorIndex) => {
  let colorIndex = index % NOTE_COLORS.length;
  if (colorIndex === prevColorIndex) {
    colorIndex = (colorIndex + 1) % NOTE_COLORS.length;
  }
  return { className: NOTE_COLORS[colorIndex], colorIndex };
};

const NotesSection = () => {
  const { t } = useI18n();
  const { setActiveView } = useDashboard();
  const { notes, loading, loadingMore, error, hasMore, loadMore, refetch } = useNotes();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showView, setShowView] = useState(false);

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  const observer = useRef();
  const lastNoteRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });

    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore, loadMore]);

  const currentUserId = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return u?.id || null;
    } catch (e) {
      return null;
    }
  }, []);

  const handleCreate = async (payload) => {
    return noteService.create(payload).then(async (response) => {
      notify('success', t('pages.file_manager.notes.success.created'));
      await refetch();
      return response;
    });
  };

  const handleEdit = async (payload) => {
    if (!selectedNote) return;
    return noteService
      .update(selectedNote.id, payload)
      .then(async (response) => {
        notify('success', t('pages.file_manager.notes.success.updated'));
        await refetch();
        return response;
      });
  };

  const openDeleteConfirm = (note) => {
    setSelectedNote(note);
    setConfirmDeleteOpen(true);
    setConfirmDeleteLoading(false);
  };

  const closeDeleteModal = () => {
    setConfirmDeleteOpen(false);
    setSelectedNote(null);
    setConfirmDeleteLoading(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedNote) return;
    setConfirmDeleteLoading(true);
    try {
      await noteService.remove(selectedNote.id);
      notify('success', t('pages.file_manager.notes.success.deleted'));
      closeDeleteModal();
      await refetch();
    } catch (err) {
      notify('error', err.message || t('pages.file_manager.notes.errors.delete_failed'));
      setConfirmDeleteLoading(false);
    }
  };

  return (
    <div className="notes notes-section-container">
      <div className="dashboard-section-header">
        <h1 className="dashboard-section-title">
          <BackButton
            onClick={() => setActiveView('dashboard')}
            ariaLabel={t('common.navigation.back_to_dashboard')}
          />
          {t('pages.file_manager.notes.title')}
        </h1>
      </div>

      {loading ? (
        <NotesSkeleton count={6} />
      ) : error ? (
        <div className="notes-error">{error}</div>
      ) : (
        <>
          <div className="notes-grid">
            <div
              className="note-card create-card"
              role="button"
              aria-label="Create new note"
              onClick={() => setShowCreate(true)}
            >
              <div className="create-icon">
                <FaPlus />
              </div>
              <div className="create-label">{t('pages.file_manager.notes.create_new')}</div>
            </div>
            {notes.map((note, index) => {
              const isOwner =
                note?.owner?.id &&
                currentUserId &&
                note.owner.id === currentUserId;
              const prevColorIndex = index > 0 ? ((index - 1) % NOTE_COLORS.length) : -1;
              const { className: colorClass } = getNoteColor(index, prevColorIndex);
              const isLast = index === notes.length - 1;
              return (
                <div
                  key={note.id}
                  ref={isLast ? lastNoteRef : null}
                  className={`note-card ${colorClass}`}
                  role="article"
                  aria-label={`Note ${note.title}`}
                >
                  <div className="note-card-content">
                    <h3 className="note-title">{note.title}</h3>
                    <p className="note-excerpt">
                      {(note.content || '').slice(0, 140)}
                      {(note.content || '').length > 140 ? '…' : ''}
                    </p>
                    <div className="note-meta">
                      <span className={`badge ${note.visibility}`}>
                        {note.visibility === 'personal' ? <img src={personalBadgeIcon} alt="" width={15} height={14} /> : <FaUsers />}
                        {note.visibility === 'personal' ? t('pages.file_manager.notes.personal') : t('pages.file_manager.notes.team')}
                      </span>
                      {note.team && (
                        <span className="badge team">
                          <FaUsers />
                          {note.team?.name}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="note-card-footer">
                    <div className="note-actions">
                      <button
                        className="icon-btn"
                        aria-label={`View ${note.title}`}
                        onClick={() => {
                          setSelectedNote(note);
                          setShowView(true);
                        }}
                      >
                        <img src={eyeIcon} alt="" width={24} height={24} />
                      </button>
                      {isOwner && (
                        <>
                          <button
                            className="icon-btn"
                            aria-label={`Share ${note.title}`}
                            onClick={() => {
                              setSelectedNote(note);
                              setShowShare(true);
                            }}
                          >
                            <img src={shareFatIcon} alt="" width={24} height={24} />
                          </button>
                          <button
                            className="icon-btn"
                            aria-label={`Edit ${note.title}`}
                            onClick={() => {
                              setSelectedNote(note);
                              setShowEdit(true);
                            }}
                          >
                            <img src={notePencilIcon} alt="" width={24} height={24} />
                          </button>
                          <button
                            className="icon-btn danger"
                            aria-label={`Delete ${note.title}`}
                            onClick={() => openDeleteConfirm(note)}
                          >
                            <img src={trashIcon} alt="" width={24} height={24} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Pagination skeleton cards - INSIDE the grid to fill remaining row spaces */}
            {loadingMore && (
              Array.from({ length: 3 }).map((_, index) => (
                <div key={`skeleton-${index}`} className="skeleton-note-card skeleton-note-card-inline">
                  <div className="skeleton-note-content">
                    <div className="skeleton-note-title" />
                    <div className="skeleton-note-excerpt">
                      <div className="skeleton-note-line" />
                      <div className="skeleton-note-line" />
                      <div className="skeleton-note-line skeleton-note-line-short" />
                    </div>
                    <div className="skeleton-note-badges">
                      <div className="skeleton-badge" />
                    </div>
                  </div>
                  <div className="skeleton-note-footer">
                    <div className="skeleton-note-actions">
                      <div className="skeleton-icon-btn" />
                      <div className="skeleton-icon-btn" />
                      <div className="skeleton-icon-btn" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      <NoteFormModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreate}
      />

      <NoteFormModal
        isOpen={showEdit}
        onClose={() => {
          setShowEdit(false);
          setSelectedNote(null);
        }}
        onSubmit={handleEdit}
        initialData={selectedNote}
      />

      <DeleteNoteModal
        isOpen={confirmDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        note={selectedNote}
        loading={confirmDeleteLoading}
      />

      <NoteShareModal
        isOpen={showShare}
        onClose={() => {
          setShowShare(false);
          setSelectedNote(null);
        }}
        note={selectedNote}
        onShareUpdated={() => refetch()}
      />

      <NoteViewModal
        isOpen={showView}
        onClose={() => {
          setShowView(false);
          setSelectedNote(null);
        }}
        note={selectedNote}
      />
    </div>
  );
};

export default NotesSection;
